import { FC } from 'react';

import { buildSanityImageUrl } from '@lib/sanity/image';
import type {
  AnswerLayout,
  SanityQuizOption,
  SanityQuizStepBlock,
  SanityQuizTheme
} from '@lib/sanity/types';

type QuizStepBlockProps = SanityQuizStepBlock & {
  theme?: SanityQuizTheme;
  selectedValue?: string;
  onAnswer: (value: string) => void;
  onNext?: () => void;
  onBack?: () => void;
};

const PillGridOptions: FC<{
  options: SanityQuizOption[];
  selectedValue?: string;
  onAnswer: (value: string) => void;
}> = ({ options, selectedValue, onAnswer }) => (
  <div className="flex flex-wrap justify-center gap-2.5">
    {options.map((option) => {
      const isSelected = selectedValue === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onAnswer(option.value)}
          className={`font-body transition-all duration-200 py-2.5 px-[22px] rounded-full text-sm font-semibold border-2 ${
            isSelected
              ? 'border-primary-600 bg-primary-600 text-white scale-105'
              : 'border-neutral-300 bg-transparent text-neutral-700 scale-100'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

const ColourSwatchOptions: FC<{
  options: SanityQuizOption[];
  selectedValue?: string;
  onAnswer: (value: string) => void;
}> = ({ options, selectedValue, onAnswer }) => (
  <div className="grid grid-cols-4 gap-4">
    {options.map((option) => {
      const isSelected = selectedValue === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onAnswer(option.value)}
          className={`aspect-square transition-all duration-200 rounded-xl ${
            isSelected
              ? 'border-[3px] border-neutral-800 outline-[3px] outline-neutral-100 scale-[1.08]'
              : 'border-[3px] border-transparent scale-100'
          }`}
          style={{
            backgroundColor: option.colour ?? '#ccc',
            outlineOffset: isSelected ? '-6px' : undefined,
            boxShadow: isSelected
              ? '0 8px 24px rgba(0,0,0,0.15)'
              : '0 2px 8px rgba(0,0,0,0.06)'
          }}
          aria-label={option.label}
        />
      );
    })}
  </div>
);

const EmojiListOptions: FC<{
  options: SanityQuizOption[];
  selectedValue?: string;
  onAnswer: (value: string) => void;
}> = ({ options, selectedValue, onAnswer }) => (
  <div className="flex flex-col gap-3">
    {options.map((option) => {
      const isSelected = selectedValue === option.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onAnswer(option.value)}
          className={`flex items-center gap-4 text-left font-body transition-all duration-200 py-4 px-5 rounded-lg text-sm font-medium border-2 ${
            isSelected
              ? 'border-primary-600 bg-primary-50 text-primary-700 translate-x-1'
              : 'border-neutral-300 bg-white text-neutral-700 translate-x-0'
          }`}
        >
          {option.emoji && <span className="text-xl">{option.emoji}</span>}
          <span>{option.label}</span>
        </button>
      );
    })}
  </div>
);

const Grid2ColOptions: FC<{
  options: SanityQuizOption[];
  selectedValue?: string;
  onAnswer: (value: string) => void;
}> = ({ options, selectedValue, onAnswer }) => (
  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    {options.map((option) => {
      const isSelected = selectedValue === option.value;
      const iconUrl = option.icon
        ? buildSanityImageUrl(option.icon).width(48).height(48).url()
        : null;

      return (
        <button
          key={option.value}
          type="button"
          onClick={() => onAnswer(option.value)}
          className={`flex items-center gap-3 text-left font-body transition-all duration-200 py-3.5 px-[18px] rounded-lg border-2 text-sm font-medium ${
            isSelected
              ? 'border-primary-600 bg-primary-50 text-primary-700'
              : 'border-neutral-300 bg-white text-neutral-700'
          }`}
        >
          {iconUrl && (
            <img
              src={iconUrl}
              alt=""
              className="h-10 w-10 flex-shrink-0 rounded"
            />
          )}
          <span>{option.label}</span>
        </button>
      );
    })}
  </div>
);

const LAYOUT_COMPONENTS: Record<
  AnswerLayout,
  FC<{
    options: SanityQuizOption[];
    selectedValue?: string;
    onAnswer: (value: string) => void;
  }>
> = {
  'grid-2col': Grid2ColOptions,
  'pill-grid': PillGridOptions,
  'colour-swatch': ColourSwatchOptions,
  'emoji-list': EmojiListOptions
};

const QuizStepBlock: FC<QuizStepBlockProps> = ({
  question,
  answerLayout = 'grid-2col',
  options,
  nextButtonText,
  selectedValue,
  onAnswer,
  onNext,
  onBack
}) => {
  const OptionsComponent = LAYOUT_COMPONENTS[answerLayout];
  const hasSelection = Boolean(selectedValue);

  return (
    <section className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md flex-col items-center justify-center px-6 py-12">
      <h2
        className="mb-10 text-center font-heading font-black text-neutral-800 leading-tight tracking-tight"
        style={{
          fontSize: 'clamp(1.5rem, 4vw, 2rem)'
        }}
      >
        {question}
      </h2>

      <div className="w-full">
        <OptionsComponent
          options={options}
          selectedValue={selectedValue}
          onAnswer={onAnswer}
        />
      </div>

      {nextButtonText && (
        <div className="mt-12 flex w-full flex-col items-center gap-4">
          <button
            type="button"
            onClick={onNext}
            disabled={!hasSelection}
            className={`w-full font-body transition-all duration-300 py-4 px-8 rounded text-sm font-semibold tracking-wide ${
              hasSelection
                ? 'bg-primary-600 text-white cursor-pointer'
                : 'bg-neutral-300 text-neutral-400 cursor-not-allowed'
            }`}
            style={{
              boxShadow: hasSelection
                ? '0 4px 16px rgba(0, 157, 229, 0.3)'
                : 'none'
            }}
          >
            {nextButtonText}
          </button>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="font-body text-sm font-medium text-neutral-500 bg-transparent border-none cursor-pointer tracking-wide uppercase"
            >
              &larr; Back
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default QuizStepBlock;
